import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Loader2, Plus, Pencil, Trash2, Users, ClipboardList, Copy } from 'lucide-react';
import { isProjoConfigured } from '@/services/db/projo';
import { getTodayManifest } from '@/services/db/pob';
import {
  getPreviousDSRWorkforce,
  copyWorkforceFromDSR,
  type PreviousDSRWorkforce,
} from '@/services/db/workforce';
import ProjoImportModal from './ProjoImportModal';
import POBImportModal, { POB_NOTE_TAG_RE } from './POBImportModal';
import POBManifestSelectionModal from './POBManifestSelectionModal';
import type { POBManifest } from '@/types/pob';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/store/authStore';
import {
  getPersonnelList,
  savePersonnelRow,
  deletePersonnelRow,
  type PersonnelSummaryRow,
} from '@/services/db/dsrEntry';

const CREW_TYPES: { value: string; label: string }[] = [
  { value: 'operator', label: 'Operator' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'service_company', label: 'Service Company' },
];

const entrySchema = z.object({
  company_name: z.string().min(1, 'Company name is required'),
  crew_type: z.string().min(1, 'Crew type is required'),
  personnel_count: z
    .string()
    .min(1, 'Count is required')
    .regex(/^\d+$/, 'Must be a whole number'),
  sse_count: z
    .string()
    .optional()
    .refine((v) => !v || /^\d+$/.test(v), 'Must be a whole number'),
  supervisor_name: z.string().optional(),
  notes: z.string().optional(),
});

type EntryFormValues = z.infer<typeof entrySchema>;

const EMPTY: EntryFormValues = {
  company_name: '',
  crew_type: '',
  personnel_count: '',
  sse_count: '',
  supervisor_name: '',
  notes: '',
};

interface Props {
  dsrId: string;
  isReadOnly?: boolean;
  projectId?: string;
  projoProjectId?: string | null;
  reportDate?: string | null;
}

export default function WorkforceForm({
  dsrId,
  isReadOnly,
  projectId,
  projoProjectId,
  reportDate,
}: Props) {
  const tenantId = useAuthStore((s) => s.tenantId);
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'closed' | 'adding' | { editing: PersonnelSummaryRow }>(
    'closed',
  );
  const [projoOpen, setProjoOpen] = useState(false);
  const [pobOpen, setPobOpen] = useState(false);
  const [manifestPickerOpen, setManifestPickerOpen] = useState(false);
  const [activeManifest, setActiveManifest] = useState<POBManifest | null>(null);
  const [copyOpen, setCopyOpen] = useState(false);

  // ARCH-029: PROJO import is hidden until a tenant-level PROJO module flag
  // is provisioned. POB is the primary workforce import path; PROJO is an
  // optional contractor-management integration.
  const projoModuleEnabled = false;

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['entry_personnel', dsrId],
    queryFn: () => getPersonnelList(dsrId),
    staleTime: 30 * 1000,
  });

  // Why: any manifest status (draft or published) is acceptable for
  // *importing* crew into the DSR. Publishing is still required for the
  // high-SSE alert path (see alert-engine), but UI should not gate import
  // on a flag the SSR often hasn't flipped yet.
  const { data: todaysManifest } = useQuery({
    queryKey: ['pob_today_any', projectId, reportDate],
    queryFn: () => getTodayManifest(projectId!, reportDate ?? undefined),
    enabled: !!projectId,
    staleTime: 60 * 1000,
  });

  const { data: previousWorkforce } = useQuery({
    queryKey: ['previous_workforce', projectId, reportDate],
    queryFn: () =>
      getPreviousDSRWorkforce(projectId!, reportDate!),
    enabled: !!projectId && !!reportDate,
    staleTime: 60 * 1000,
  });

  const totalHeadcount = entries.reduce((sum, e) => sum + e.personnel_count, 0);
  const operatorCount = entries.filter((e) => e.crew_type === 'operator').reduce((s, e) => s + e.personnel_count, 0);
  const contractorCount = entries.filter((e) => e.crew_type === 'contractor').reduce((s, e) => s + e.personnel_count, 0);
  const serviceCount = entries.filter((e) => e.crew_type === 'service_company').reduce((s, e) => s + e.personnel_count, 0);

  // Exact SSE totals: prefer the sse_count column (populated by POB
  // import + copy-from-previous-day); fall back to the legacy
  // `[POB:sse=M/N]` notes tag for rows written pre-S68b; finally fall
  // back to the "named supervisor" heuristic for manual entries.
  const sseTotals = entries.reduce(
    (acc, e) => {
      if (e.sse_count != null) {
        acc.hasExact = true;
        acc.exactSSE += e.sse_count;
        acc.exactTotal += e.personnel_count;
        return acc;
      }
      const m = e.notes?.match(POB_NOTE_TAG_RE);
      if (m) {
        acc.hasExact = true;
        acc.exactSSE += parseInt(m[1], 10);
        acc.exactTotal += parseInt(m[2], 10);
      }
      return acc;
    },
    { hasExact: false, exactSSE: 0, exactTotal: 0 }
  );
  const namedSseCount = entries.filter((e) => e.supervisor_name?.trim()).length;
  const namedSsePct =
    entries.length > 0 ? Math.round((namedSseCount / entries.length) * 100) : 0;
  const exactSsePct =
    sseTotals.exactTotal > 0
      ? Math.round((sseTotals.exactSSE / sseTotals.exactTotal) * 100)
      : 0;

  const onSaved = () => {
    setMode('closed');
    queryClient.invalidateQueries({ queryKey: ['entry_personnel', dsrId] });
    queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
  };

  const onDelete = async (id: string) => {
    if (!confirm('Remove this crew entry?')) return;
    try {
      await deletePersonnelRow(id);
      queryClient.invalidateQueries({ queryKey: ['entry_personnel', dsrId] });
      queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not delete entry');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-mozaic-teal" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Workforce / Personnel</h2>
        <p className="text-xs text-muted-foreground">
          Headcount by company and crew type. SSE = named supervisor.
        </p>
      </div>

      {/* ARCH-029: POB is the primary workforce import path. PROJO is
          hidden unless the tenant has the PROJO module provisioned. */}
      {/* POB import (Walk S51, S76d) — today's POB as primary shortcut
          when available, plus an always-available picker for any
          published manifest (KI-048 / KI-049). */}
      {!isReadOnly && projectId && todaysManifest && (
        <Button
          variant="default"
          className="w-full bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
          onClick={() => {
            setActiveManifest(todaysManifest);
            setPobOpen(true);
          }}
        >
          <ClipboardList className="mr-2 h-4 w-4" />
          Import from Today's POB ({todaysManifest.total_pob ?? 0} on board)
        </Button>
      )}
      {!isReadOnly && projectId && (
        <Button
          variant="outline"
          className="w-full border-mozaic-teal text-mozaic-teal hover:bg-mozaic-teal/10"
          onClick={() => setManifestPickerOpen(true)}
        >
          <ClipboardList className="mr-2 h-4 w-4" />
          Import from POB Manifest…
        </Button>
      )}

      {/* Copy from Previous Day — shown when a prior DSR on this project
          has workforce entries. Draft or submitted, either works. */}
      {!isReadOnly && previousWorkforce && (
        <Button
          variant="outline"
          className="w-full border-mozaic-teal text-mozaic-teal hover:bg-mozaic-teal/10"
          onClick={() => setCopyOpen(true)}
        >
          <Copy className="mr-2 h-4 w-4" />
          Copy from Previous Day (
          {format(
            new Date(previousWorkforce.dsr.report_date + 'T00:00:00'),
            'MMM d',
          )}
          , {previousWorkforce.entries.length} entries)
        </Button>
      )}

      {/* PROJO import (Walk P2) — hidden unless tenant PROJO module is on */}
      {projoModuleEnabled && !isReadOnly && isProjoConfigured && projoProjectId && (
        <Button
          variant="outline"
          className="w-full border-mozaic-teal text-mozaic-teal hover:bg-mozaic-teal/10"
          onClick={() => setProjoOpen(true)}
        >
          <Users className="mr-2 h-4 w-4" />
          Import from PROJO Registry
        </Button>
      )}
      {projoModuleEnabled && !isReadOnly && isProjoConfigured && !projoProjectId && (
        <div className="rounded-md border border-dashed border-input bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          Connect this project to PROJO to import workforce data automatically.{' '}
          <a href="/settings/projects" className="font-semibold text-mozaic-teal hover:underline">
            Open project settings →
          </a>
        </div>
      )}

      {!isReadOnly && tenantId && previousWorkforce && (
        <CopyPreviousDayModal
          open={copyOpen}
          onClose={() => setCopyOpen(false)}
          source={previousWorkforce}
          dsrId={dsrId}
          tenantId={tenantId}
          onCopied={() => {
            queryClient.invalidateQueries({ queryKey: ['entry_personnel', dsrId] });
            queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
          }}
        />
      )}

      {!isReadOnly && projectId && (
        <POBManifestSelectionModal
          open={manifestPickerOpen}
          onClose={() => setManifestPickerOpen(false)}
          projectId={projectId}
          onSelect={(m) => {
            setActiveManifest(m);
            setPobOpen(true);
          }}
        />
      )}

      {!isReadOnly && tenantId && activeManifest && (
        <POBImportModal
          open={pobOpen}
          onClose={() => {
            setPobOpen(false);
            setActiveManifest(null);
          }}
          manifest={activeManifest}
          dsrId={dsrId}
          tenantId={tenantId}
          onImported={() => {
            queryClient.invalidateQueries({ queryKey: ['entry_personnel', dsrId] });
            queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
          }}
        />
      )}

      {!isReadOnly && tenantId && projoProjectId && (
        <ProjoImportModal
          open={projoOpen}
          onClose={() => setProjoOpen(false)}
          projoProjectId={projoProjectId}
          dsrId={dsrId}
          tenantId={tenantId}
          reportDate={reportDate ?? undefined}
          onImported={() => {
            queryClient.invalidateQueries({ queryKey: ['entry_personnel', dsrId] });
            queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
          }}
        />
      )}

      {/* Summary */}
      {entries.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Stat value={totalHeadcount} label="Total POB" tone="teal" />
              <Stat value={operatorCount} label="Operator" />
              <Stat value={contractorCount} label="Contractor" />
              <Stat value={serviceCount} label="Service" />
            </div>
            {sseTotals.hasExact ? (
              <p className="mt-4 border-t pt-3 text-sm text-muted-foreground">
                SSE%:{' '}
                <span className="font-semibold text-foreground">{exactSsePct}%</span>{' '}
                <span className="text-xs">
                  ({sseTotals.exactSSE} SSEs of {sseTotals.exactTotal} total)
                </span>
              </p>
            ) : (
              <p className="mt-4 border-t pt-3 text-sm text-muted-foreground">
                SSE Coverage:{' '}
                <span className="font-semibold text-foreground">{namedSsePct}%</span>{' '}
                <span className="text-xs">
                  ({namedSseCount} of {entries.length} crews with named SSE)
                </span>
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {entries.length === 0 && mode === 'closed' && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No crew entries yet. Add a crew below.
          </CardContent>
        </Card>
      )}

      {/* Cards */}
      {entries.map((entry) => (
        <CrewCard
          key={entry.id}
          entry={entry}
          onEdit={() => setMode({ editing: entry })}
          onDelete={() => onDelete(entry.id)}
          isReadOnly={isReadOnly}
        />
      ))}

      {/* Inline form */}
      {!isReadOnly && mode !== 'closed' && tenantId && (
        <CrewEntryForm
          dsrId={dsrId}
          tenantId={tenantId}
          editing={mode !== 'adding' ? mode.editing : undefined}
          onSaved={onSaved}
          onCancel={() => setMode('closed')}
        />
      )}

      {/* Add button */}
      {!isReadOnly && mode === 'closed' && (
        <Button
          variant="outline"
          className="w-full border-dashed border-mozaic-teal text-mozaic-teal"
          onClick={() => setMode('adding')}
        >
          <Plus className="mr-1 h-4 w-4" /> Add Crew Entry
        </Button>
      )}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function Stat({
  value,
  label,
  tone,
}: {
  value: number;
  label: string;
  tone?: 'teal';
}) {
  return (
    <div className="text-center">
      <div
        className={`text-2xl font-bold ${
          tone === 'teal' ? 'text-mozaic-teal' : 'text-mozaic-navy'
        }`}
      >
        {value}
      </div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function CrewCard({
  entry,
  onEdit,
  onDelete,
  isReadOnly,
}: {
  entry: PersonnelSummaryRow;
  onEdit: () => void;
  onDelete: () => void;
  isReadOnly?: boolean;
}) {
  const meta = CREW_TYPES.find((c) => c.value === entry.crew_type);
  const pobTag = entry.notes?.match(POB_NOTE_TAG_RE);
  const displayNotes = entry.notes?.replace(POB_NOTE_TAG_RE, '').trim() || null;
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-2 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate font-semibold text-mozaic-navy">{entry.company_name}</h3>
              {pobTag && (
                <Badge className="bg-mozaic-teal/10 text-mozaic-teal border border-mozaic-teal/40">
                  Imported from POB
                </Badge>
              )}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {entry.personnel_count} personnel
              {entry.supervisor_name ? ` · SSE: ${entry.supervisor_name}` : ''}
            </p>
            {displayNotes && (
              <p className="mt-1 truncate text-xs text-muted-foreground">{displayNotes}</p>
            )}
          </div>
          <Badge className="bg-mozaic-navy text-white">{meta?.label ?? entry.crew_type}</Badge>
        </div>
        {!isReadOnly && (
          <div className="mt-3 flex gap-2 border-t pt-3">
            <Button variant="ghost" size="sm" className="text-mozaic-teal" onClick={onEdit}>
              <Pencil className="mr-1 h-3 w-3" /> Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={onDelete}
            >
              <Trash2 className="mr-1 h-3 w-3" /> Delete
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CrewEntryForm({
  dsrId,
  tenantId,
  editing,
  onSaved,
  onCancel,
}: {
  dsrId: string;
  tenantId: string;
  editing?: PersonnelSummaryRow;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<EntryFormValues>({
    resolver: zodResolver(entrySchema),
    defaultValues: editing
      ? {
          company_name: editing.company_name,
          crew_type: editing.crew_type,
          personnel_count: String(editing.personnel_count),
          sse_count:
            editing.sse_count != null ? String(editing.sse_count) : '',
          supervisor_name: editing.supervisor_name ?? '',
          notes: editing.notes ?? '',
        }
      : EMPTY,
  });

  const onSubmit = handleSubmit(async (values) => {
    setSaving(true);
    try {
      const parsedSSE =
        values.sse_count && values.sse_count.trim().length > 0
          ? parseInt(values.sse_count, 10)
          : null;
      await savePersonnelRow({
        id: editing?.id,
        dsrReportId: dsrId,
        tenantId,
        companyName: values.company_name,
        crewType: values.crew_type,
        personnelCount: parseInt(values.personnel_count, 10),
        sseCount: parsedSSE,
        supervisorName: values.supervisor_name || null,
        notes: values.notes || null,
      });
      onSaved();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not save crew entry');
    } finally {
      setSaving(false);
    }
  });

  return (
    <Card className="border-2 border-mozaic-teal">
      <CardContent className="space-y-4 pt-6">
        <h3 className="font-semibold">{editing ? 'Edit Crew Entry' : 'New Crew Entry'}</h3>

        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Company Name <span className="text-mozaic-red">*</span>
          </Label>
          <Controller
            control={control}
            name="company_name"
            render={({ field }) => <Input {...field} placeholder="e.g. Acme Drilling Co." />}
          />
          {errors.company_name && (
            <p className="text-xs text-mozaic-red">{errors.company_name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Crew Type <span className="text-mozaic-red">*</span>
          </Label>
          <Controller
            control={control}
            name="crew_type"
            render={({ field }) => (
              <div className="flex gap-2">
                {CREW_TYPES.map((opt) => {
                  const selected = field.value === opt.value;
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => field.onChange(opt.value)}
                      className={`flex-1 rounded-lg border px-3 py-2 text-xs font-semibold transition-colors ${
                        selected
                          ? 'border-mozaic-teal bg-mozaic-teal text-white'
                          : 'border-input bg-background hover:bg-muted'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
          />
          {errors.crew_type && (
            <p className="text-xs text-mozaic-red">{errors.crew_type.message}</p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Headcount <span className="text-mozaic-red">*</span>
            </Label>
            <Controller
              control={control}
              name="personnel_count"
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  placeholder="0"
                />
              )}
            />
            {errors.personnel_count && (
              <p className="text-xs text-mozaic-red">{errors.personnel_count.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              SSE Count
            </Label>
            <Controller
              control={control}
              name="sse_count"
              render={({ field }) => (
                <Input
                  {...field}
                  value={field.value ?? ''}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  placeholder="0"
                />
              )}
            />
            {errors.sse_count && (
              <p className="text-xs text-mozaic-red">{errors.sse_count.message}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Supervisor / SSE Name
          </Label>
          <Controller
            control={control}
            name="supervisor_name"
            render={({ field }) => (
              <Input {...field} value={field.value ?? ''} placeholder="e.g. J. Smith" />
            )}
          />
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Notes
          </Label>
          <Controller
            control={control}
            name="notes"
            render={({ field }) => (
              <Textarea
                {...field}
                value={field.value ?? ''}
                rows={2}
                placeholder="Any notes about this crew group…"
              />
            )}
          />
        </div>

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            className="flex-1 bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
            onClick={onSubmit}
            disabled={saving}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? 'Update Entry' : 'Save Entry'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function CopyPreviousDayModal({
  open,
  onClose,
  source,
  dsrId,
  tenantId,
  onCopied,
}: {
  open: boolean;
  onClose: () => void;
  source: PreviousDSRWorkforce;
  dsrId: string;
  tenantId: string;
  onCopied: () => void;
}) {
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const doCopy = async () => {
    setCopying(true);
    setError(null);
    try {
      await copyWorkforceFromDSR({
        targetDsrId: dsrId,
        tenantId,
        sourceEntries: source.entries,
      });
      onCopied();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Copy failed');
    } finally {
      setCopying(false);
    }
  };

  const dateLabel = format(
    new Date(source.dsr.report_date + 'T00:00:00'),
    'MMM d, yyyy',
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl">
        <div className="border-b px-6 py-4">
          <div className="flex items-center gap-2">
            <Copy className="h-5 w-5 text-mozaic-teal" />
            <h2 className="font-semibold text-mozaic-navy">
              Copy workforce from {dateLabel}?
            </h2>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {source.entries.length} crew {source.entries.length === 1 ? 'entry' : 'entries'} will be
            copied into this DSR. Notes are cleared — SSE counts and
            supervisor names come with.
          </p>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <ul className="divide-y text-sm">
            {source.entries.map((e) => (
              <li key={e.id} className="flex items-center justify-between py-2">
                <div className="min-w-0">
                  <p className="truncate font-medium text-mozaic-navy">
                    {e.company_name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {e.crew_type}
                    {e.supervisor_name ? ` · SSE: ${e.supervisor_name}` : ''}
                  </p>
                </div>
                <div className="ml-4 text-right text-sm">
                  <div>
                    <span className="font-semibold">{e.personnel_count}</span>{' '}
                    <span className="text-xs text-muted-foreground">ppl</span>
                  </div>
                  {e.sse_count != null && (
                    <div className="text-xs text-muted-foreground">
                      {e.sse_count} SSE
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
        {error && (
          <p className="px-6 pb-2 text-sm text-mozaic-red">{error}</p>
        )}
        <div className="flex items-center justify-end gap-2 border-t px-6 py-4">
          <Button variant="outline" onClick={onClose} disabled={copying}>
            Cancel
          </Button>
          <Button
            className="bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
            onClick={doCopy}
            disabled={copying}
          >
            {copying ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Copy Entries'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
